"use strict";

import { EventDebouncer, EventDebouncerAnimationFrame } from './event-debouncer.js';

const createNodeFromHTML = (htmlString) =>
{
	return (new DOMParser()).parseFromString(htmlString, 'text/html').body.children[0] || null;
};

class SlideShow
{
	constructor(itemContainer)
	{
		this.this = this;
		
		this.sliderContainer = null;
		this.containerWidth = 0;
		this.itemWidth = 0;
		this.items = null;
		this.numItemsInViewport = 0;
		this.maxLeft = 0;
		this.minLeft = 0;
		this.currentLeft = 0;
		this.currentIndex = 0;
		this.touchDisabled = false;
		this.touchMoving = false;
		this.snapThreshold = 25;
		this.addControlSliders = true;
		this.addOverlays = true;
		this.backSlider = createNodeFromHTML('<div class="backSlider icon icon-angle-left"></div>');
		this.forwardSlider = createNodeFromHTML('<div class="forwardSlider icon icon-angle-right"></div>');
		this.overlayLeft = createNodeFromHTML('<div class="overlay left"></div>');
		this.overlayRight = createNodeFromHTML('<div class="overlay right"></div>');
		this.isTransitioning = false;
		
		this.itemContainer = null;
		
		this.initialized = false;
		
		this.startX = 0;
		this.xDiff = 0;
		
		this.startY = 0;
		this.yDiff = 0;
		
		this.originalLeft = 0;
		this.threshold = 1;
		
		this.scrollby = false;
		this.scrollbyIsset = false;
		
		this.moveDebouncer = new EventDebouncerAnimationFrame(() =>
		{
			if(this.touchMoving) this.setLeft(this.originalLeft - this.xDiff);
		});
		
		this.sliderContainerWidth = 0;
		this.windowHeight = 0;
		
		
		
		this.itemContainer = itemContainer;
	}
	
	initialize()
	{
		if(this.initialized)
		{
			throw new Error("Slide show has already been initialized!");
		}

		this.itemContainer.classList.add('slider');

		this.sliderContainer = document.createElement('div');
		this.sliderContainer.classList.add('sliderContainer');

		// Transfer child nodes from itemContainer to sliderContainer
		while(this.itemContainer.children.length)
		{
			this.sliderContainer.appendChild(this.itemContainer.children[0]);
		}

		// Set event listeners for navigation buttons
		if(this.backSlider !== null) this.backSlider.addEventListener('click', this.handleBackClick.bind(this));
		if(this.forwardSlider !== null) this.forwardSlider.addEventListener('click', this.handleForwardClick.bind(this));

		if(this.addControlSliders)
		{
			this.itemContainer.appendChild(this.backSlider);
			this.itemContainer.appendChild(this.forwardSlider);
		}

		if(this.addOverlays)
		{
			this.itemContainer.appendChild(this.overlayLeft);
			this.itemContainer.appendChild(this.overlayRight);
		}

		this.itemContainer.appendChild(this.sliderContainer);

		this.calculate();

		this.touchMoving = false;

		this.setLeft(this.currentLeft);

		// Recalculate positions on device change
		window.addEventListener('bm:bsDeviceChange', this.handleDeviceChange.bind(this));

		// Touch event listeners
		this.itemContainer.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
		this.itemContainer.addEventListener('touchmove', this.touchMove.bind(this), { passive: false });
		this.itemContainer.addEventListener('touchend', this.touchEnd.bind(this), { passive: true });

		this.initialized = true;
	}

	handleBackClick(event)
	{
		if(!this.touchMoving)
		{
			this.back();
		}
	}

	handleForwardClick(event)
	{
		if(!this.touchMoving)
		{
			this.forward();
		}
	}

	handleDeviceChange(event)
	{
		this.calculate();
		this.setLeft(this.minMaxLeft(-(this.itemWidth * this.currentIndex)));
	}
	
	setLeft(newLeft, animate = false)
	{
		// Handle infinite loop when reaching the end
		if(this.isAtEnd() && newLeft < this.minLeft)
		{
			const firstItem = this.sliderContainer.firstChild;
			
			newLeft += this.itemWidth;
			this.originalLeft += this.itemWidth;

			// Adjust position and possibly delay reset for smooth transition
			if(!this.touchMoving)
			{
				setTimeout(() => this.setLeft(this.minLeft, true), 20);
				
				newLeft += this.itemWidth;
				animate = false;
			}

			this.sliderContainer.appendChild(firstItem);
		}
		
		// Handle infinite loop when reaching the start
		else if(this.isAtStart() && newLeft > this.maxLeft)
		{
			const lastItem = this.sliderContainer.lastChild;
			
			newLeft -= this.itemWidth;
			this.originalLeft -= this.itemWidth;

			// Adjust position and possibly delay reset for smooth transition
			if(!this.touchMoving)
			{
				setTimeout(() => this.setLeft(this.maxLeft, true), 20);
				
				newLeft -= this.itemWidth;
				animate = false;
			}

			this.sliderContainer.prepend(lastItem);
		}

		this.currentLeft = this.minMaxLeft(newLeft);

		// Apply or remove slide transition based on the animate flag
		this.sliderContainer.classList[animate ? 'add' : 'remove']('slideTransition');

		// Handle transition state
		if(animate)
		{
			this.isTransitioning = true;
			
			setTimeout(() => { this.isTransitioning = false; }, parseFloat(getComputedStyle(this.sliderContainer)['transitionDuration']) * 1000);
		}
		
		// Apply the new position to the slider
		this.sliderContainer.style.transform = `translateX(${this.currentLeft}%)`;
	}
	
	minMaxLeft(value)
	{
		return Math.min(this.maxLeft, Math.max(this.minLeft, value));
	}

	calculate()
	{
		this.items = this.sliderContainer.children;
		this.containerWidth = parseInt(this.itemContainer.offsetWidth, 10);
		this.itemWidth = (this.items[0].offsetWidth / this.containerWidth) * 100;
		this.numItemsInViewport = Math.floor(100 / this.itemWidth);
		this.minLeft = -(this.itemWidth * this.items.length) + 100;
		
		this.autoHideSliders();
	}

	autoHideSliders()
	{
		const hide = this.items.length <= this.numItemsInViewport;
		
		this.touchDisabled = this.touchDisabled || hide;
		
		if(this.backSlider === null || this.forwardSlider === null) return;
		 
		this.backSlider.classList.toggle('hidden', hide);
		
		this.forwardSlider.classList.toggle('hidden', hide);
	}

	forward(steps = 1, animate = true)
	{
		this.currentIndex = Math.min(this.currentIndex + steps, this.items.length - this.numItemsInViewport);
		
		this.setLeft(this.currentLeft - this.itemWidth * steps, animate);
	}

	back(steps = 1, animate = true)
	{
		this.currentIndex = Math.max(this.currentIndex - steps, 0);
		
		this.setLeft(this.currentLeft + this.itemWidth * steps, animate);
	}

	isAtStart()
	{
		const isAtStart = this.currentLeft === this.maxLeft;
		
		this.itemContainer.classList.toggle('isAtStart', isAtStart);
		
		return isAtStart;
	}

	isAtEnd()
	{
		const isAtEnd = this.currentLeft === this.minLeft;
		
		this.itemContainer.classList.toggle('isAtEnd', isAtEnd);
		
		return isAtEnd;
	}

	scrollToStart()
	{
		this.currentIndex = 0;
		
		this.setLeft(this.maxLeft, true);
	}

	scrollToEnd()
	{
		this.currentIndex = this.items.length - Math.floor(100 / this.itemWidth);
		
		this.setLeft(this.minLeft, true);
	}
	
	getTouchEvent(event)
	{
		if(typeof event.targetTouches === 'undefined' && typeof event.originalEvent !== 'undefined')
		{
			return event.originalEvent;
		}
		return event;
	}

	touchStart(event)
	{
		if(this.touchDisabled) return;
		
		event = this.getTouchEvent(event);
		
		if(event.targetTouches.length > 1) return;
		
		if(!this.touchMoving)
		{
			this.xDiff = 0;
			this.yDiff = 0;
			this.startX = event.touches[0].clientX;
			this.startY = event.touches[0].clientY;
			this.originalLeft = this.currentLeft;
			this.threshold = this.snapThreshold / 100 * this.itemWidth;
			this.scrollbyIsset = false;
			this.scrollby = false;
			
			this.sliderContainerWidth = this.sliderContainer.offsetWidth;
			this.windowHeight = window.outerHeight;
		}
	}

	touchMove(event)
	{
		if(this.touchDisabled) return;
		
		event = this.getTouchEvent(event);
		
		if(event.targetTouches.length > 1) return;
		
		if(this.scrollbyIsset === false)
		{
			const radian = Math.abs(Math.atan((event.touches[0].clientY - this.startY) / (event.touches[0].clientX - this.startX)));
			const DEGREE_THRESHOLD = 45;  // Threshold angle in degrees
			this.scrollby = (radian * (180 / Math.PI)) > DEGREE_THRESHOLD;  // Convert radians to degrees
			this.scrollbyIsset = true;
		}
		
		if(this.scrollby === false)
		{
			this.touchMoving = true;
			this.xDiff = (this.startX - event.touches[0].clientX) / this.sliderContainerWidth * 100;
			this.yDiff = (this.startY - event.touches[0].clientY) / this.windowHeight * 100;
			event.preventDefault();
			this.moveDebouncer.event();
		}
	}

	touchEnd(event)
	{
		if(this.touchDisabled) return;
		
		event = this.getTouchEvent(event);
		
		if(event.targetTouches && event.targetTouches.length > 1) return;
		
		if(!this.touchMoving) return;
		
		this.currentLeft = this.originalLeft;
		
		const extraIndex = Math.max(0, Math.floor(((Math.abs(this.xDiff) / this.itemWidth) * 100) / 100));
		
		if(this.xDiff < -this.threshold)
		{
			this.back(1 + extraIndex);
		}
		else if(this.xDiff > this.threshold)
		{
			this.forward(1 + extraIndex);
		}
		else
		{
			this.setLeft(this.originalLeft, true);
		}
		
		this.touchMoving = false;
	}
	
	autoScroll(interval)
	{
		// Set an interval to click forward every x seconds
		this.autoScroller = setInterval(this.handleForwardClick.bind(this), interval*1000);
	}
}

export default SlideShow;